<template>

  <div class="row">
    <div class="col-12">
      <b-tabs v-model="tabIndex">
        <b-tab>
          <template #title>
            {{ $t('last3Month') }}
          </template>
        </b-tab>
        <b-tab>
          <template #title>
            {{ $t('older') }}
          </template>
        </b-tab>
      </b-tabs>
    </div>
    <div class="col-12">
      <div class="tile">
        <div class="form-inline">
          <client-select v-if="!client" class="mr-1 mb-2" :model="filterFormModel" attribute="client" @change="search"
            :place-holder="$t('selectClient')"></client-select>
          <client-user-group-select v-if="filterFormModel.client" class="mr-1 mb-2" :model="filterFormModel"
            attribute="group" :client-filter="filterFormModel.client" @change="search"
            :place-holder="$t('selectGroup')"></client-user-group-select>
          <client-user-select v-if="filterFormModel.client" class="mr-1 mb-2" :model="filterFormModel" attribute="user"
            :client-filter="filterFormModel.client" :group-filter="filterFormModel.group" @change="search"
            :place-holder="$t('selectUser')"></client-user-select>
          <time-range-picker v-if="tabIndex === 0" :model="filterFormModel" :label="$t('time')" class="mr-1 mb-2"
            :defaultShortcut="8"></time-range-picker>
          <form-input :model="filterFormModel" class="mr-1 mb-2" attribute="keyword"
            :placeholder="$t('keyword')"></form-input>
          <form-select v-if="tabIndex === 1" :model="filterFormModel" class="mr-1 mb-2" attribute="month"
            :options="monthOptions"></form-select>
          <form-select :model="filterFormModel" class="mr-1 mb-2" attribute="direction" :options="[
        { value: null, text: $t('allCallDirection') },
        { value: '1', text: $t('incoming') },
        { value: '2', text: $t('outgoing') },
        { value: '3', text: $t('local') },
      ]"></form-select>
          <form-select :model="filterFormModel" class="mr-1 mb-2" attribute="callStatus" :options="[
        { value: null, text: $t('allCallStatus') },
        { value: 'missCall', text: $t('missCall') },
        { value: 'outboundSuccess', text: $t('outboundSuccess') },
        { value: 'outboundFailed', text: $t('outboundFailed') },
      ]"></form-select>
          <button @click="search" class="btn btn-primary mr-1 mb-2">{{ $t('search') }}</button>
          <button @click="reset" class="btn btn-secondary mr-1 mb-2">Reset</button>
        </div>
      </div>
    </div>
    <div class="col-12" v-show="filterFormModel.client">
      <div class="c-paging clearfix">
        <div class="c-paging__count" v-if="itemCount">
          {{ $t('found', [itemCount]) }}
        </div>
        <div class="c-paging pull-right" size="sm" v-if="tabIndex === 1">
          <ul class="pagination pagination pagination-sm justify-content-center b-pagination pagination-sm">
            <li class="page-item" v-if="filterFormModel.page > 1">
              <button @click="backPage" class="page-link">‹‹ {{ $t('backToPageOne') }}</button>
            </li>
            <li v-if="filterFormModel.page" class="page-item disabled">
              <span class="page-link">{{ filterFormModel.page }}</span>
            </li>
            <li class="page-item" v-if="nextToken">
              <button @click="nextPage" class="page-link">› {{ $t('nextPage') }}</button>
            </li>
          </ul>
        </div>
        <div class="c-paging__right">
          <div class="c-paging__numbers">
            <b-pagination v-if="itemCount > filterFormModel.pageSize" class="pull-right" size="sm"
              v-model="filterFormModel.page" :total-rows="itemCount" :per-page="filterFormModel.pageSize"
              @input="load"></b-pagination>
          </div>
        </div>
      </div>
    </div>
    <div v-if="firstLoading" class="col-12 text-center">
      <div class="tile">
        <h4>{{ $t('firstLoadingGuide') }}</h4>
      </div>
    </div>
    <div class="col-12" v-else-if="items && items.length">
      <div class="c-smart-table tile">
        <div class="row c-smart-table__title">
          <div class="col-lg-1"></div>
          <div class="col-lg-2">{{ $t('from') }}</div>
          <div class="col-lg-2">{{ $t('to') }}</div>
          <div class="col-lg-3">{{ $t('callDetail') }}</div>
          <div class="col-lg-3">{{ $t('hangupCause') }}</div>
          <div class="col-lg-1"></div>
        </div>
        <div class="row" v-for="item in items" :key="item._id">
          <div class="col-lg-1 text-center">
            <font-awesome-icon :class="iconClass(item)" size="2x" :icon="icon(item)" />
            {{ $t(item.direction) }}
          </div>
          <div class="col-lg-2">
            <p><b class="c-smart-table__label">{{ $t('from') }}: </b></p>
            <p><b>{{ $t('client') }}: </b><span>{{ item.client && item.client.domain }}</span></p>
            <p v-if="item.direction === 'inbound'">
              <b>{{ $t('number') }}:&nbsp;</b>
              <span>{{ item.fromNumber }}</span>
            </p>
            <p v-if="item.direction === 'outbound'">
              <b>{{ $t('number') }}:&nbsp;</b><span>{{ item.fromNumber }}</span>
            </p>
            <p v-if="item.direction === 'outbound' || item.direction === 'local'">
              <b>{{ $t('ext') }}:&nbsp;</b><span>{{ item.fromExt }}</span>
            </p>
          </div>
          <div class="col-lg-2">
            <p><b class="c-smart-table__label">{{ $t('to') }}: </b></p>
            <p v-if="item.direction === 'inbound'">
              <b>{{ $t('number') }}:&nbsp;</b><span>{{ item.toNumber }}</span>
            </p>
            <p v-if="item.direction === 'outbound'">
              <b>{{ $t('number') }}:&nbsp;</b>
              <span>{{ item.toNumber }}</span>
            </p>
            <p v-if="item.direction === 'inbound' || item.direction === 'local'">
              <b>{{ $t('ext') }}:&nbsp;</b><span>{{ item.toExt }}</span>
            </p>
          </div>
          <div class="col-lg-3">
            <p><b class="c-smart-table__label">{{ $t('callDetail') }}: </b></p>
            <p>
              <b>{{ $t('startTime') }}:&nbsp;</b><span>{{ item.startTime | moment('HH:mm:ss DD/MM/YYYY') }}</span>
            </p>
            <p v-if="item.answerTime">
              <b>{{ $t('answerTime') }}:&nbsp;</b><span>{{ item.answerTime | moment('HH:mm:ss DD/MM/YYYY') }}</span>
            </p>
            <p v-if="item.endTime">
              <b>{{ $t('endTime') }}:&nbsp;</b><span>{{ item.endTime | moment('HH:mm:ss DD/MM/YYYY') }}</span>
            </p>
            <p v-if="item.campaign">
              <b>{{ $t('callCampaign') }}: </b>
              <span>{{ item.campaign.name }} </span>
            </p>
          </div>
          <div class="col-lg-3">
            <p>
              <b>{{ $t('duration') }}:&nbsp;</b><span>{{ item.duration | callDuration }}</span>&nbsp;|&nbsp;
              <b>{{ $t('billDuration') }}:&nbsp;</b><span>{{ item.billDuration | callDuration }}</span>&nbsp;|&nbsp;
              <span>({{ item.hangupCause }})</span>
              <template v-if="item.audioQuality">
                &nbsp;|&nbsp;<b>{{ $t('audioQuality') }}:&nbsp;</b><span>{{ item.audioQuality }}</span>
              </template>
              <template
                v-if="item.direction === 'inbound' && (item.hangupCause !== 'NORMAL_CLEARING' || !item.billDuration)">
                <span>
                  &nbsp;|&nbsp;<b class="text-danger">{{ $t('missCall') }}</b>
                </span>
              </template>
            </p>
            <app-audio v-if="item.recordingDownloaded" :id="item._id" :duration="item.duration" />
            <p v-if="item.transcripts && item.transcripts.length" class="small">
              <span>{{ $t('content') }}:</span> {{ item.transcripts.join(", ") }}
            </p>
            <p v-else-if="item.billDuration" class="small">{{ $t('noTranscript') }}</p>
          </div>
          <div class="col-lg-1">
            <div class="c-smart-table__content">
              <div class="c-buttons clearfix is-2">
                <div class="c-buttons__col">
                  <button type="button" v-clipboard:copy="item._id" v-clipboard:success="onCopy"
                    v-clipboard:error="onError" class="btn btn-sm btn-info mr-1">
                    <font-awesome-icon icon="copy" />&nbsp;Copy ID
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="col-12 text-center text-danger" v-else>{{ $t('notFoundSearch') }}</div>
    <div class="col-12" v-if="itemCount" v-show="filterFormModel.client">
      <div class="c-paging__count">
        {{ $t('found', [itemCount]) }}
      </div>
      <div class="c-paging pull-right" size="sm" v-if="tabIndex === 1">
        <ul class="pagination pagination pagination-sm justify-content-center b-pagination pagination-sm">
          <li class="page-item" v-if="filterFormModel.page > 1">
            <button @click="backPage" class="page-link">‹‹ {{ $t('backToPageOne') }}</button>
          </li>
          <li v-if="filterFormModel.page" class="page-item disabled">
            <span class="page-link">{{ filterFormModel.page }}</span>
          </li>
          <li class="page-item" v-if="nextToken">
            <button @click="nextPage" class="page-link">› {{ $t('nextPage') }}</button>
          </li>
        </ul>
      </div>
      <b-pagination v-if="itemCount > filterFormModel.pageSize && tabIndex === 0" class="pull-right" size="sm"
        v-model="filterFormModel.page" :total-rows="itemCount" :per-page="filterFormModel.pageSize" @input="load">
      </b-pagination>
    </div>
  </div>
</template>

<script>
import list from '@/mixins/List';
import utils from '@/utils';
import ClientSelect from "@/components/ClientSelect";
import ClientUserSelect from "@/components/ClientUserSelect";
import ClientUserGroupSelect from "@/components/ClientUserGroupSelect2";
import TimeRangePicker from "@/components/TimeRangePicker";
import AppAudio from "@/components/AppAudio";
import _ from 'lodash';
import moment from "moment";

export default {
  components: { TimeRangePicker, ClientSelect, AppAudio, ClientUserSelect, ClientUserGroupSelect },
  mixins: [list, utils],
  props: ['contactId', 'number', 'client'],
  data() {
    return {
      tabIndex: 0,
      userRole: this.$store.state.user.role,
      service: "/call",
      defaultFilterFormModel: {
        direction: null,
        callStatus: null,
        client: this.client ? this.client._id : null,
        user: null,
        group: null,
        keyword: '',
        host: null,
        gateway: null,
        page: 1,
        queryExecutionId: null,
        nextToken: null
      },
      showRecording: false,
      recordingUrl: null,
      firstLoading: true,
      queryExecutionId: null,
      nextToken: null,
      monthOptions: [{ value: null, text: this.$t('time') }],
      limitTimeRange: moment().subtract(90, 'day').startOf('day').format('x'),
      clientCreateTime: null,
    }
  },
  methods: {
    search: _.debounce(function () {
      this.firstLoading = false;
      this.filterFormModel.page = 1;
      this.filterFormModel.queryExecutionId = null;
      this.filterFormModel.nextToken = null;
      this.load();
    }, 200),
    icon(item) {
      if (item.direction === 'inbound') {
        return 'sign-in-alt';
      }
      if (item.direction === 'outbound') {
        return 'sign-out-alt';
      }
      if (item.direction === 'local') {
        return 'sync-alt';
      }
    },
    iconClass(item) {
      if (item.hangupCause === 'NORMAL_CLEARING') {
        return 'text-success';
      } else {
        return 'text-danger';
      }
    },
    listen(id) {
      let loader = this.$loading.show();
      this.$service.get(`/call/${id}/recording`).then(response => {
        this.showRecording = true;
        this.recordingUrl = response.data.url;
        this.$refs.player.play();
      }).catch(error => {
        if (error.response.data.msg) {
          this.$swal({
            text: error.response.data.msg,
            icon: 'error'
          });
        }
      }).finally(() => {
        loader.hide();
      });
    },
    reset: function () {
      // this.filterFormModel = {};
      Object.assign(this.filterFormModel, this.defaultFilterFormModel);
      this.firstLoading = true;
      if (this.client) {
        this.search();
      }
    },
    onCopy() {
      this.$swal({
        text: this.$t('copySuccess'),
        icon: 'success',
        toast: true,
        position: 'top-end',
        timer: 5000,
      });
    },
    onError() {
      this.$swal({
        title: this.$t('error'),
        text: this.$t('copyFailed'),
        icon: 'error'
      });
    },
    nextPage() {
      this.filterFormModel.page += 1;
      this.filterFormModel.queryExecutionId = this.queryExecutionId;
      this.filterFormModel.nextToken = this.nextToken;
      this.load();
    },
    backPage() {
      this.filterFormModel.page = 1;
      this.filterFormModel.queryExecutionId = this.queryExecutionId;
      this.filterFormModel.nextToken = null;
      this.load();
    },
    async getMonthOptions(clientId) {

      if (!clientId) {
        return;
      }
      const client = await this.$bgService.get(`/client/${clientId}`);
      const clientCreateTime = parseInt(client.data.createTime);

      const startDate = moment(clientCreateTime).startOf('day');
      const endDate = moment().subtract(90, 'day').startOf('day');

      const monthOptions = []
      if (endDate.isBefore(startDate)) {
        return;
      }
      while (startDate.isBefore(endDate)) {
        monthOptions.push({ value: startDate.format("YYYY-MM"), text: `Tháng ` + startDate.format("MM/YYYY") });
        startDate.add(1, 'month');
      }
      this.monthOptions = [{ value: null, text: this.$t('time') }].concat(monthOptions.reverse());
    },
  },
  mounted() {
    this.service = this.tabIndex === 1 ? "/lake-call" : '/call';
    if (this.client) {
      this.search();
      this.getMonthOptions(this.client)
    }
  },
  watch: {
    tabIndex: function (tabIndex) {
      this.service = tabIndex === 1 ? "/lake-call" : '/call';
      if (this.client || this.filterFormModel.client) {
        this.search();
      }
    },
    "filterFormModel.client": "getMonthOptions",
    monthOptions: function (monthOptions) {
      if (monthOptions.length > 1) {
        this.filterFormModel.month = monthOptions[1].value;
      }
    },
    items: function (items) {
      this.queryExecutionId = items[0].queryExecutionId;
      this.nextToken = items[0].nextToken;
    }
  },
  i18n: {
    messages: {
      en: {
        all: 'All calls',
        number: 'Number',
        ext: 'Extension',
        contact: 'Contact',
        ticket: 'Ticket',
        user: 'Agent',
        incoming: 'Incoming',
        outgoing: 'Outgoing',
        local: 'Internal',
        from: 'From',
        to: 'To',
        callDetail: 'Call detail',
        direction: 'Direction',
        duration: 'Duration',
        billDuration: 'Talk duration',
        startTime: 'Start',
        answerTime: 'Answer',
        endTime: 'End',
        hangupCause: 'End status',
        NORMAL_CLEARING: 'Successfully',
        USER_BUSY: 'Busy',
        ORIGINATOR_CANCEL: 'Caller cancel',
        NO_USER_RESPONSE: 'No answer',
        listenRecording: 'Recording',
        createContact: 'Create new contact',
        createTicket: 'Create new ticket',
        addToTicket: 'Add to existing ticket',
        content: 'Content',
        fail: 'Fail to connect',
        noTranscript: 'No transcript recorded',
        toNumber: 'To number',
        fromNumber: 'From number',
        allCallDirection: 'Call direction: All',
        allCallStatus: 'Status: All',
        audioQuality: 'Audio quality',
        na: 'Not available',
        missCall: 'Miss call',
        outboundSuccess: 'Outbound success',
        outboundFailed: 'Outbound failed',
        firstLoadingGuide: 'Choose filters then press Search to start browsing call history',
        callCampaign: 'Call campaign',
        copySuccess: 'Copy success',
        copyFailed: 'Copy failed',
        selectUser: "Select user",
        selectGroup: "Select group",
        backToPageOne: "Back to page 1",
        nextPage: "Next page",
        last3Month: "Last 3 month",
        older: "Older",
      },
      vi: {
        all: 'Tất cả',
        number: 'Số',
        ext: 'Nhánh',
        contact: 'Khách',
        ticket: 'Ticket',
        user: 'Nhân viên',
        incoming: 'Gọi vào',
        outgoing: 'Gọi ra',
        local: 'Gọi nội bộ',
        from: 'Gọi từ',
        to: 'Gọi đến',
        callDetail: 'Chi tiết cuộc gọi',
        direction: 'Hướng gọi',
        duration: 'Tổng thời gian',
        billDuration: 'Đàm thoại',
        startTime: 'Bắt đầu',
        answerTime: 'Trả lời',
        endTime: 'Kết thúc',
        hangupCause: 'Kết quả',
        NORMAL_CLEARING: 'Thành công',
        USER_BUSY: 'Máy bận',
        ORIGINATOR_CANCEL: 'Người gọi dừng',
        NO_USER_RESPONSE: 'Không nghe máy',
        listenRecording: 'Nghe ghi âm',
        createContact: 'Tạo KH mới',
        createTicket: 'Tạo ticket mới',
        addToTicket: 'Thêm vào ticket',
        content: 'Nội dung',
        fail: 'Kết nối thất bại',
        noTranscript: 'Không ghi nhận được nội dung',
        toNumber: 'Gọi ra đến số',
        fromNumber: 'Gọi ra từ số',
        allCallDirection: 'Hướng gọi: Tất cả',
        allCallStatus: 'Trạng thái: Tất cả',
        audioQuality: 'Chất lượng gọi',
        na: 'Không có thông tin',
        missCall: 'Gọi nhỡ',
        outboundSuccess: 'Gọi ra thành công',
        outboundFailed: 'Gọi ra thất bại',
        firstLoadingGuide: 'Chọn các bộ lọc và nhấn Tìm kiếm để xem lịch sử gọi',
        callCampaign: 'Chiến dịch',
        copyId: 'Sao chép Id',
        copySuccess: 'Sao chép thành công',
        copyFailed: 'Sao chép thất bại ',
        selectUser: "Chọn người dùng",
        selectGroup: "Chọn nhóm",
        backToPageOne: "Quay lại trang 1",
        nextPage: "Trang tiếp theo",
        last3Month: "3 tháng gần nhất",
        older: "Cũ hơn",
      }
    }
  }
}
</script>